import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Building2, Plus, Trash2 } from 'lucide-react';
import type { Site } from '../../../lib/api';
import { CURRENCY_OPTIONS, getAnnualSpendOptions } from './types';
import type { SupplierFormData } from './types';

interface WizardStep2DetailsProps {
  formData: SupplierFormData;
  onInputChange: (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[]) => void;
  previewMode: boolean;
  customerSites: Site[];
}

/**
 * CR-5390: Supplier Details restructure.
 *  - Field order: Supplier Name → structured Address → Currency → Annual Spend → Description.
 *  - Address split into Line 1, Line 2 (optional), City, County/Region (optional),
 *    Postcode (mandatory), Country (optional).
 *  - The previous top-of-form Customer dropdown is removed: every customer
 *    company is added through the Customer Companies section below, which is
 *    always visible (was hidden when no extras existed). The first selected
 *    company is treated as the primary by submission code (customer_ids[0]).
 */
export default function WizardStep2Details({
  formData,
  onInputChange,
  previewMode,
  customerSites,
}: WizardStep2DetailsProps) {
  const linkedIds = formData.customer_ids || [];
  const availableSites = customerSites.filter((s) => !linkedIds.includes(s.id));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Supplier Details</h2>
        <p className="text-muted-foreground">
          Enter the supplier company information. Fields marked with <span className="text-red-500">*</span> are mandatory.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="company_name">Supplier Name <span className="text-red-500">*</span></Label>
          <Input
            id="company_name"
            value={formData.company_name}
            onChange={(e) => onInputChange('company_name', e.target.value)}
            placeholder="Enter supplier name"
            disabled={previewMode}
            required
          />
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="address_line_1">Address Line 1 <span className="text-red-500">*</span></Label>
          <Input
            id="address_line_1"
            value={formData.address_line_1 || ''}
            onChange={(e) => onInputChange('address_line_1', e.target.value)}
            placeholder="Street address"
            disabled={previewMode}
            required
          />
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="address_line_2">Address Line 2</Label>
          <Input
            id="address_line_2"
            value={formData.address_line_2 || ''}
            onChange={(e) => onInputChange('address_line_2', e.target.value)}
            placeholder="Apartment, suite, etc. (optional)"
            disabled={previewMode}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_city">City / Town</Label>
          <Input
            id="address_city"
            value={formData.address_city || ''}
            onChange={(e) => onInputChange('address_city', e.target.value)}
            placeholder="City or town"
            disabled={previewMode}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_region">County / Region</Label>
          <Input
            id="address_region"
            value={formData.address_region || ''}
            onChange={(e) => onInputChange('address_region', e.target.value)}
            placeholder="County or region (optional)"
            disabled={previewMode}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_postcode">Postcode <span className="text-red-500">*</span></Label>
          <Input
            id="address_postcode"
            value={formData.address_postcode || ''}
            onChange={(e) => onInputChange('address_postcode', e.target.value)}
            placeholder="Postcode / ZIP"
            disabled={previewMode}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_country">Country</Label>
          <Input
            id="address_country"
            value={formData.address_country || ''}
            onChange={(e) => onInputChange('address_country', e.target.value)}
            placeholder="Country"
            disabled={previewMode}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency">Currency <span className="text-red-500">*</span></Label>
          <Select
            value={formData.currency}
            onValueChange={(v) => onInputChange('currency', v)}
            disabled={previewMode}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select currency..." />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">(for dashboard conversions)</p>
        </div>

        {/* CR-3943: Annual Spend as dropdown instead of text input */}
        <div className="space-y-2">
          <Label htmlFor="annual_spend">Annual Spend <span className="text-red-500">*</span></Label>
          <Select
            value={formData.annual_spend}
            onValueChange={(v) => onInputChange('annual_spend', v)}
            disabled={previewMode}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select annual spend range..." />
            </SelectTrigger>
            <SelectContent>
              {getAnnualSpendOptions(formData.currency).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Enter description"
            rows={4}
            disabled={previewMode}
          />
        </div>
      </div>

      {/* CR-5390: renamed Additional Companies → Customer Companies and
          consolidated all customer selection here so there's no duplicate
          dropdown at the top of the form. */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Customer Companies <span className="text-red-500">*</span></h3>
            <p className="text-sm text-muted-foreground">Select the customer companies this supplier serves. The first selection becomes the primary customer for risk-question routing.</p>
          </div>
          {availableSites.length > 0 && (
            <Select
              value=""
              onValueChange={(v) => {
                const siteId = Number(v);
                const site = customerSites.find((s) => s.id === siteId);
                const next = [...linkedIds, siteId];
                onInputChange('customer_ids', next);
                // Mirror the primary onto the legacy customer_id / name fields
                // so downstream code that still reads them keeps working.
                if (next.length === 1) {
                  onInputChange('customer_id', site?.company ?? siteId);
                  onInputChange('customer_name', site?.name || '');
                }
              }}
              disabled={previewMode}
            >
              <SelectTrigger className="w-[240px]">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  <span>{linkedIds.length === 0 ? 'Add Customer Company' : 'Add Another Company'}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {availableSites.map((site) => (
                  <SelectItem key={site.id} value={site.id.toString()}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {linkedIds.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
            <Building2 className="h-10 w-10 mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-muted-foreground">No customer companies selected yet. Add at least one to continue.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {linkedIds.map((siteId, idx) => {
              const site = customerSites.find((s) => s.id === siteId);
              const isPrimary = idx === 0;
              return (
                <div key={siteId} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{site?.name || `Site #${siteId}`}</span>
                    {isPrimary && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Primary</span>
                    )}
                    {site?.city && (
                      <span className="text-sm text-muted-foreground">— {site.city}{site.country ? `, ${site.country}` : ''}</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const next = linkedIds.filter((id) => id !== siteId);
                      onInputChange('customer_ids', next);
                      // If we removed the primary, reset legacy customer_id / name
                      // to the new primary (or clear if none left).
                      if (isPrimary) {
                        const newPrimary = next[0];
                        if (newPrimary) {
                          const newPrimarySite = customerSites.find((s) => s.id === newPrimary);
                          onInputChange('customer_id', newPrimarySite?.company ?? newPrimary);
                          onInputChange('customer_name', newPrimarySite?.name || '');
                        } else {
                          onInputChange('customer_id', null);
                          onInputChange('customer_name', '');
                        }
                      }
                    }}
                    disabled={previewMode}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
